import { useEffect, useRef, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import IconButton from '@mui/material/IconButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CircularProgress from '@mui/material/CircularProgress';
import SendIcon from '@mui/icons-material/Send';
import SignatureCanvas from 'react-signature-canvas';
import { useAuthIsVerified, AuthEnsureUser, formatDate, formatAddress } from '@footsteps/shared';
import { Header, Footer, Section } from '../components';
import { Attachments } from '../components/attachments';
import { routes } from '../routes';
import { useWozCase, useWozCaseManager } from '../components/woz-case-manager/woz-case-manager.provider';
import { RegisterUser } from '../components/register-user';
import { getWozCategoryTitle } from '../components/woz-case-manager/woz-category-titles';
import type { WozCaseComplaint } from '../components/woz-case-manager/woz-case/woz-case.types';
import { getWozLatestValue } from '../utils/woz';

const ComplaintCardSkeleton = (): JSX.Element => (
  <div className='card-section card-grid-full complaint-card'>
    <Skeleton variant='text' width='45%' height={24} />
    <div className='complaint-card-body'>
      <div className='complaint-detail'>
        <Skeleton variant='text' width='15%' height={14} sx={{ mb: 0.5 }} />
        <Skeleton variant='text' width='40%' height={16} />
      </div>
      <div className='complaint-detail'>
        <Skeleton variant='text' width='25%' height={14} sx={{ mb: 0.5 }} />
        <Skeleton variant='text' width='100%' height={16} />
        <Skeleton variant='text' width='95%' height={16} />
        <Skeleton variant='text' width='60%' height={16} />
      </div>
    </div>
  </div>
);

const SummaryContent = (): JSX.Element => {
  const isVerified = useAuthIsVerified();
  const { caseState, caseViewModel } = useWozCase();
  const { caseManagerViewModel } = useWozCaseManager();
  const navigate = useNavigate();
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);

  const {
    data,
    isFormatting,
    hasUnformattedComplaints,
    shareConsent,
    contactConsent,
    attachments,
    isLoadingAttachments,
    isUploadingAttachment,
    deleteComplaintSequence,
    isSubmitting,
    submitIssues,
    submitError,
  } = caseState;
  const kadaster = data?.kadaster ?? null;
  const latestWozValue = kadaster?.wozValues ? getWozLatestValue(kadaster.wozValues) : null;
  const dateSigned = data?.dateSigned ?? formatDate(new Date().toISOString(), 'yyyy-MM-dd');
  const signatureSaved = !!data?.signatureData;

  const caseAttachments = attachments.filter((a) => a.source === 'case');
  const getComplaintAttachments = (complaint: WozCaseComplaint) =>
    attachments.filter((a) => a.source === 'complaint' && a.caseComplaintId === complaint.id);

  useEffect(() => {
    if (isVerified) {
      if (!data?.dateSigned) {
        caseViewModel.setDateSigned(formatDate(new Date().toISOString(), 'yyyy-MM-dd'));
      }
      if (data?.signatureData && sigCanvasRef.current) {
        sigCanvasRef.current.fromDataURL(data.signatureData);
      }
    } else {
      caseViewModel.setShareConsent(false);
      caseViewModel.setContactConsent(false);
      sigCanvasRef.current?.clear();
    }
  }, [isVerified, data?.signatureData, data?.dateSigned, caseViewModel]);

  const caseId = data?.id;
  useEffect(() => {
    if (isVerified && caseId) caseViewModel.loadAttachments();
  }, [isVerified, caseId, caseViewModel]);

  useEffect(() => {
    if (hasUnformattedComplaints && !isFormatting) {
      caseViewModel.formatComplaints();
    }
  }, [hasUnformattedComplaints, isFormatting, caseViewModel]);

  const handleAddComplaint = () => {
    caseViewModel.reopenFlow();
    navigate(routes.intake);
  };

  const handleSaveSignature = () => {
    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) return;
    const base64 = sigCanvasRef.current.toDataURL('image/png');
    caseViewModel.updateSignature(base64, dateSigned);
  };

  const handleClearSignature = () => {
    sigCanvasRef.current?.clear();
    caseViewModel.clearSignature();
  };

  return (
    <div className='page-wrapper'>
      <Header />
      <Section paddingY='medium' maxWidth='wide' contentClassName='summary-content'>
        <h1 className='summary-title'>Samenvatting</h1>

        <div className='card-section card-grid-full notice-card'>
          <div className='notice-card-content'>
            <InfoOutlinedIcon className='notice-card-icon' />
            <div>
              <h2 className='heading-3'>Bewzaar indienen en/of later verdergaan?</h2>
              {isVerified ? (
                <p className='text-body'>
                  Je e-mailadres is geverifieerd. <br />
                  Je voortgang wordt automatisch opgeslagen en je kunt op elk moment verdergaan waar je gebleven was.
                  <br />
                  Nog niet verstuurde bezwaren worden automatisch verwijderd <strong>na 30 dagen</strong> inactiviteit.
                </p>
              ) : (
                <p className='text-body'>
                  Wil je je bezwaar indienen of later verdergaan? <br />
                  Verifieer eerst je e-mailadres hieronder bij <strong>contactgegevens</strong> <br />
                  Je krijg binnen enkele minuten een verificatie code via e-mail. <br />
                  Een <strong>geverifieerd e-mailadres</strong> is ook nodig om je bezwaar te kunnen indienen bij de
                  gemeente en/of je voortgang te kunnen opslaan.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className='card-grid'>
          <div className='card-section'>
            <h2 className='heading-3'>Objectinformatie</h2>
            <div className='info-list'>
              <div className='info-row'>
                <span className='text-muted'>Adres</span>
                <span className='text-value'>{formatAddress(data, { fallback: 'Onbekend' })}</span>
              </div>
              <div className='info-row'>
                <span className='text-muted'>WOZ-objectnummer</span>
                <span className='text-value'>{kadaster?.wozObjectNumber ?? 'Onbekend'}</span>
              </div>
              <div className='info-row'>
                <span className='text-muted'>WOZ-waarde</span>
                <span className='text-value'>
                  {latestWozValue
                    ? `${latestWozValue.assessedValue} (peildatum ${latestWozValue.referenceDate})`
                    : 'Onbekend'}
                </span>
              </div>
              <div className='info-row'>
                <span className='text-muted'>Woningtype</span>
                <span className='text-value'>{kadaster?.usagePurposes?.join(', ') ?? 'Onbekend'}</span>
              </div>
              <div className='info-row'>
                <span className='text-muted'>Bouwjaar</span>
                <span className='text-value'>{kadaster?.constructionYear ?? 'Onbekend'}</span>
              </div>
              <div className='info-row'>
                <span className='text-muted'>Gebruiksoppervlakte</span>
                <span className='text-value'>{kadaster?.gboM2 ? `${kadaster.gboM2} m²` : 'Onbekend'}</span>
              </div>
              <div className='info-row'>
                <span className='text-muted'>Perceeloppervlakte</span>
                <span className='text-value'>{kadaster?.plotArea ? `${kadaster.plotArea} m²` : 'Onbekend'}</span>
              </div>
            </div>
          </div>

          <div className='card-section'>
            <h3 className='heading-3'>Contactgegevens</h3>
            {!isVerified && <p className='text-body'>Vul je contactgegevens in om door te gaan.</p>}
            <div className='summary-register-user-container'>
              <RegisterUser />
            </div>
          </div>

          {isVerified && data?.id && (
            <div className='card-section card-grid-full'>
              <h2 className='heading-3'>Bijlagen bij bezwaar</h2>
              <p className='text-body text-muted'>
                Voeg algemene bijlagen toe aan je bezwaar, of je kunt per klacht een bijlage toevoegen.
                <br />
                Je kunt in totaal maximaal <strong>30 MB</strong> aan bijlagen toevoegen.
              </p>
              <Attachments
                source='case'
                attachments={caseAttachments}
                isLoading={isLoadingAttachments}
                isUploading={isUploadingAttachment}
                disabled={!isVerified}
                onUpload={caseViewModel.uploadAttachment}
                onDelete={caseViewModel.deleteAttachment}
                onGetSignedUrl={caseViewModel.getAttachmentSignedUrl}
                onUpdateDescription={caseViewModel.updateAttachmentDescription}
              />
            </div>
          )}

          <h2 className='heading-2'>Klachten</h2>

          {caseViewModel.getGroupedComplaints().map((mainCat) => (
            <div key={mainCat.code} className='card-grid-full complaints-main-cat-group'>
              <h3 className='heading-3 complaints-type-heading'>{mainCat.title}</h3>

                  {mainCat.complaints.map((complaint) => {
                    const isLoading = isFormatting && complaint.formattedText === null;
                    const title = getWozCategoryTitle(complaint.categoryCode);
                    const originalText = complaint.correctedText ?? complaint.text;

                    if (isLoading) {
                      return <ComplaintCardSkeleton key={complaint.sequence} />;
                    }

                    return (
                      <div key={complaint.sequence} className='card-section card-grid-full complaint-card'>
                        <div className='complaint-card-header'>
                          <h5 className='heading-5'>{title}</h5>
                          <IconButton
                            size='small'
                            onClick={() => caseViewModel.setDeleteComplaintSequence(complaint.sequence)}
                            aria-label='Klacht verwijderen'
                          >
                            <DeleteOutlineIcon fontSize='small' />
                          </IconButton>
                        </div>
                        <div className='complaint-card-body'>
                          <div className='complaint-detail'>
                            <span className='complaint-label'>Klacht</span>
                            <p className='text-body'>{originalText}</p>
                          </div>
                          {complaint.formattedText && (
                            <div className='complaint-detail'>
                              <span className='complaint-label'>Geformatteerde klacht</span>
                              <p className='text-body'>{complaint.formattedText}</p>
                            </div>
                          )}
                          {complaint.references?.length > 0 && (
                            <div className='complaint-detail'>
                              <span className='complaint-label'>Referenties</span>
                              {complaint.references.map((ref, idx) => (
                                <p key={idx} className='text-body'>
                                  {ref.value}
                                  <br />
                                  <span>Referentie uit bezwaar: &ldquo;{ref.citation}&rdquo;</span>
                                </p>
                              ))}
                            </div>
                          )}
                          {isVerified && data?.id && (
                            <div className='complaint-detail'>
                              <span className='complaint-label'>Bijlagen bij de klacht</span>
                              <Attachments
                                source='complaint'
                                caseComplaintId={complaint.id ?? undefined}
                                attachments={getComplaintAttachments(complaint)}
                                isLoading={isLoadingAttachments}
                                isUploading={isUploadingAttachment}
                                disabled={!isVerified}
                                onUpload={caseViewModel.uploadAttachment}
                                onDelete={caseViewModel.deleteAttachment}
                                onGetSignedUrl={caseViewModel.getAttachmentSignedUrl}
                                onUpdateDescription={caseViewModel.updateAttachmentDescription}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
            </div>
          ))}

          <div className='card-grid-full complaints-add'>
            <Button variant='contained' size='small' startIcon={<AddCircleOutlineIcon />} onClick={handleAddComplaint}>
              Bezwaar toevoegen
            </Button>
          </div>
        </div>

        <h2 className='heading-2'>Verzenden</h2>
        <div className={`card-section card-grid-full signature-card${!isVerified ? ' signature-card-disabled' : ''}`}>
          <h2 className='heading-3'>Ondertekening</h2>
          <p className='text-body text-muted signature-description'>
            {isVerified
              ? 'Onderteken hieronder om je bezwaar te bevestigen.'
              : 'Verifieer eerst je e-mailadres bij contactgegevens om te kunnen ondertekenen.'}
          </p>

          <fieldset disabled={!isVerified} className='signature-fieldset'>
            <div className='signature-layout'>
              <div className='signature-left'>
                <label className='form-label'>Handtekening</label>
                <div className='signature-pad-wrapper'>
                  <SignatureCanvas
                    ref={sigCanvasRef}
                    penColor='#1a1a2e'
                    onEnd={handleSaveSignature}
                    canvasProps={{
                      className: `signature-pad-canvas${!isVerified ? ' signature-pad-disabled' : ''}`,
                      style: !isVerified ? { pointerEvents: 'none' } : undefined,
                    }}
                  />
                </div>
                <div className='signature-footer'>
                  <Button
                    variant='outlined'
                    size='small'
                    startIcon={<DeleteIcon />}
                    onClick={handleClearSignature}
                    disabled={!isVerified}
                  >
                    Wissen
                  </Button>
                  {signatureSaved && <p className='text-body text-muted'>Handtekening is opgeslagen</p>}
                </div>
              </div>

              <div className='signature-right'>
                <label className='form-label'>Datum ondertekening</label>
                <TextField
                  type='date'
                  size='small'
                  value={dateSigned}
                  onChange={(e) => caseViewModel.setDateSigned(e.target.value)}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { max: formatDate(new Date().toISOString(), 'yyyy-MM-dd') },
                  }}
                  disabled={!isVerified}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={shareConsent}
                      onChange={(e) => caseViewModel.setShareConsent(e.target.checked)}
                      disabled={!isVerified}
                    />
                  }
                  label={
                    <span>
                      Delen met gemeente is toegestaan
                      <span className='checkbox-helper'>
                        Ja, ik geef toestemming om dit bezwaar te delen met de gemeente
                      </span>
                    </span>
                  }
                  className='signature-checkbox'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={contactConsent}
                      onChange={(e) => caseViewModel.setContactConsent(e.target.checked)}
                      disabled={!isVerified}
                    />
                  }
                  label={
                    <span>
                      Contact opnemen is toegestaan
                      <span className='checkbox-helper'>
                        Ja, de gemeente mag contact met mij opnemen over dit bezwaar
                      </span>
                    </span>
                  }
                  className='signature-checkbox'
                />
              </div>
            </div>
          </fieldset>
        </div>

        <div className='card-actions'>
          {isVerified && (
            <Button
              variant='contained'
              startIcon={isSubmitting ? <CircularProgress size={20} color='inherit' /> : <SendIcon />}
              onClick={async () => {
                const success = await caseViewModel.submitCase();
                if (success && data?.id) {
                  caseManagerViewModel.onCaseSubmitted();
                  navigate(`/submitted/${data.id}`, { replace: true });
                }
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Bezig met indienen...' : 'Bezwaar indienen'}
            </Button>
          )}
        </div>
      </Section>
      <Footer />

      <Dialog open={deleteComplaintSequence !== null} onClose={() => caseViewModel.setDeleteComplaintSequence(null)}>
        <DialogTitle>Klacht verwijderen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Weet je zeker dat je deze klacht wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => caseViewModel.setDeleteComplaintSequence(null)}>Annuleren</Button>
          <Button onClick={caseViewModel.confirmDeleteComplaint} color='error' variant='contained'>
            Verwijderen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={submitIssues.length > 0} onClose={() => caseViewModel.setSubmitIssues([])}>
        <DialogTitle>Bezwaar kan nog niet worden ingediend</DialogTitle>
        <DialogContent>
          <DialogContentText>Los de volgende punten op voordat je je bezwaar kunt indienen:</DialogContentText>
          <List dense>
            {submitIssues.map((issue) => (
              <ListItem key={issue}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <ErrorOutlineIcon color='error' fontSize='small' />
                </ListItemIcon>
                <ListItemText primary={issue} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => caseViewModel.setSubmitIssues([])} variant='contained'>
            Begrepen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={submitError !== null} onClose={() => caseViewModel.setSubmitError(null)}>
        <DialogTitle>Indienen mislukt</DialogTitle>
        <DialogContent>
          <DialogContentText>{submitError}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => caseViewModel.setSubmitError(null)} variant='contained'>
            Sluiten
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export const Summary = (): JSX.Element => {
  const { caseManagerState } = useWozCaseManager();
  const navigate = useNavigate();

  const isReady = caseManagerState.auth.isResolved && !caseManagerState.cases.isLoading;

  useEffect(() => {
    if (isReady && !caseManagerState.currentCase) {
      navigate(routes.home, { replace: true });
    }
  }, [isReady, caseManagerState.currentCase, navigate]);

  if (!caseManagerState.currentCase) return <></>;

  return (
    <AuthEnsureUser>
      <SummaryContent />
    </AuthEnsureUser>
  );
};
